import { Permission } from '@casops/permissions';
import { casOpsApi, QueryTag, TagList } from '../api';
import { TagDescription } from '@reduxjs/toolkit/query';
import { UserUnionModel } from './usersApi';

export interface LoginRequest {
  username: string;
  password: string;
}

export interface ForgotPasswordModel {
  email: string;
}

export interface ResetPasswordModel {
  password: string;
  confirmPassword: string;
  email: string | undefined;
  token: string | undefined;
}

export interface ChangePasswordModel {
  currentPassword: string;
  newPassword: string;
}

export interface MeModel extends UserUnionModel {
  displayName: string;
  initials: string;
  permissions: Permission[];
}

const loginUrl = '/auth/login';
const meUrl = '/auth/me';
const logoutUrl = '/auth/logout';
const forgotPasswordUrl = '/auth/forgotpassword';
const resetPasswordUrl = '/auth/resetpassword';
const changePasswordUrl = '/auth/me/password';

const authTags: TagDescription<QueryTag.UserAuth>[] = [{ type: QueryTag.UserAuth, id: TagList }];

export const authApi = casOpsApi.injectEndpoints({
  endpoints: build => ({
    login: build.mutation<MeModel, LoginRequest>({
      query: credentials => ({
        url: loginUrl,
        method: 'POST',
        body: credentials,
      }),
      invalidatesTags: [QueryTag.SettingsUi],
    }),
    me: build.query<MeModel, void>({
      query: () => meUrl,
      // The auth state must be invalidated when the site changes.
      providesTags: authTags,
    }),
    forgotPassword: build.mutation<void, ForgotPasswordModel>({
      query: credentials => ({
        url: forgotPasswordUrl,
        method: 'POST',
        body: credentials,
      }),
    }),
    logout: build.mutation<void, void>({
      query: () => ({
        url: logoutUrl,
        method: 'POST',
      }),
    }),
    changePassword: build.mutation<void, ChangePasswordModel>({
      query: credentials => ({
        url: changePasswordUrl,
        method: 'PUT',
        body: credentials,
      }),
    }),
    resetPassword: build.mutation<void, ResetPasswordModel>({
      query: credentials => ({
        url: resetPasswordUrl,
        method: 'POST',
        body: credentials,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useLoginMutation,
  useMeQuery,
  useForgotPasswordMutation,
  useLogoutMutation,
  useChangePasswordMutation,
  useResetPasswordMutation,
} = authApi;
